<template>
  <div class="middle-content">
    <div id="newContainer">
      <!-- 客户无定位提示 -->
      <transition name="location-tip-fade">
        <div v-if="showLocationTip" class="map-location-tip">
          <i class="el-icon-warning"></i>
          <span>{{ $t('home.dashboard.customer_no_location_message') }}</span>
        </div>
      </transition>
    </div>
    <!-- 轨迹弹窗 -->
    <TrackDialog
      :visible.sync="trackDialogVisible"
      :carrier-id="trackCarrierId"
      :carrier-name="trackCarrierName"
    />
  </div>
</template>

<script>
import bus from '@/utils/bus.js'
import mapboxgl from 'mapbox-gl'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import { TempUtils } from '@/utils/TempUtil.js'
import { sensorTypeConvert } from '@/utils/dict/sensor'
import { mapTrackPosition, mapTrackGetScopeCenter } from '@/api/modules/map.js'
import { goNewWindows } from '@/utils/goNewWin'
import TrackDialog from '@/pages/home/component/bashboard/components/TrackDialog.vue'

export default {
  components: { TrackDialog },
  data () {
    return {
      sensorTypeConvert,
      icon: null,
      map: null,
      scopeId: null,
      sceneId: null,
      assetMarkers: [],
      assetMarkerElements: [], // 资产标记元素引用
      traceAssetList: [],
      sceneMarkers: [],
      sceneMarkerElements: [], // 场景标记元素引用
      sceneMapList: [],
      customerMarker: null,
      customerMarkerElement: null, // 客户标记元素引用
      customerPosition: null,
      showLocationTip: false, // 是否显示无定位提示
      openPopup: false,
      popupData: null,
      selectedMarkerElement: null, // 当前选中的地图标记元素
      refreshTimer: null, // 定时刷新计时器
      // 轨迹弹窗相关
      trackDialogVisible: false,
      trackCarrierId: null,
      trackCarrierName: ''
      // sampleValueInstances: {} // 取样值组件实例映射

    }
  },
  mounted () {
    bus.$on('checkTree', row => {
      console.log("row->",row);
      if (row && row.level !== undefined) {
        if (row.nodeType !== 0 && row.nodeType !== 1) {
          const scopeId = row.scopeId
          const sceneId = row.level === 0 ? row.scopeId : row.sceneId
          if (scopeId !== this.scopeId) {
            // 切换客户中心
            this.scopeId = scopeId
            this.sceneId = sceneId
            // loadCustomerCenter 内部会调用 getMarkerFn()，无需重复调用
            this.loadCustomerCenter()
            bus.$emit('checkScope', { scopeId: this.scopeId })
          }
        }
      }
    })

    // 监听树节点选中事件，设置对应标记的选中状态
    bus.$on('treeNodeSelected', row => {
      this.handleTreeNodeSelected(row)
    })


    bus.$on('activeCarrier', id => {
      const marker = this.assetMarkers.find(item => item.marker && item.marker.extData && item.marker.extData.id === id)
      if (marker) {
        const { lng, lat } = marker.marker.extData
        const itemLng = parseFloat(lng)
        const itemLat = parseFloat(lat)
        if (!isNaN(itemLng) && !isNaN(itemLat)) {
          this.map.setCenter([itemLng, itemLat])
        }
      }
    })

    this.scopeId = this.$store.state.SchemaTree[0].sceneCode === 'all_node' ? this.$store.state.SchemaTree.slice(1)[0].scopeId : this.$store.state.SchemaTree[0].scopeId
    this.sceneId = this.scopeId
    console.log('scopeId -> ',this.scopeId);
    console.log('sceneId -> ',this.sceneId);
    this.getInitMap()
    // 启动定时刷新（5分钟）
    this.startRefreshTimer()
  },
  beforeDestroy () {
    bus.$off('checkTree')
    bus.$off('treeNodeSelected')
    bus.$off('activeCarrier')
    // 清除定时刷新计时器
    this.stopRefreshTimer()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  watch: {
    '$i18n.locale' () {
      this.updateMapLanguage()
    }
  },
  methods: {
    // 启动定时刷新
    startRefreshTimer () {
      // 每5分钟刷新一次数据（300000毫秒）
      this.refreshTimer = setInterval(() => {
        this.refreshData()
      }, 300000)
    },
    // 停止定时刷新
    stopRefreshTimer () {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    // 刷新数据
    refreshData () {
      if (this.scopeId) {
        this.loadCustomerCenter()
        this.getMarkerFn()
      }
    },
    getMapboxLanguage () {
      const lang = this.$i18n.locale || 'zh_CN'
      if (lang === 'zh_CN') return 'zh-Hans'
      if (lang === 'en_US') return 'en'
      return 'en'
    },
    updateMapLanguage () {
      if (!this.map) return
      const language = this.getMapboxLanguage()
      const style = this.map.getStyle()
      if (!style || !style.layers) return
      style.layers.forEach(layer => {
        if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
          this.map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name_' + language], ['get', 'name']])
        }
      })
    },
    async getInitMap (retryCount = 0) {
      await this.$nextTick()
      const container = document.getElementById('newContainer')
      if (!container || container.offsetWidth === 0) {
        if (retryCount < 10) {
          console.error('Map container width is zero, retrying...', retryCount)
          setTimeout(() => this.getInitMap(retryCount + 1), 300)
          return
        } else {
          console.error('Map container width is zero after retries')
          return
        }
      }

      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN
      mapboxgl.telemetryEnabled = false
      const language = this.getMapboxLanguage()

      this.map = new mapboxgl.Map({
        container: container,
        style: 'mapbox://styles/mapbox/streets-v12',
        zoom: 16,
        attributionControl: false
      })

      const languagePlugin = new MapboxLanguage({
        defaultLanguage: language
      })
      this.map.addControl(languagePlugin)
      this.map.addControl(new mapboxgl.NavigationControl())

      this.map.on('load', () => {
        this.icon = {}
        this.loadCustomerCenter()
      })

      this.map.on('error', (e) => {
        console.error('Mapbox error:', e)
      })
    },
    loadCustomerCenter () {
      mapTrackGetScopeCenter({ scopeId: this.scopeId }).then(res => {
        var result = res.result
        if (result && result.center) {
          var lng = parseFloat(result.center.longitude)
          var lat = parseFloat(result.center.latitude)
          if (!isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
            this.customerPosition = {
              lng: lng,
              lat: lat,
              name: result.center.name || 'Customer Center',
              id: this.scopeId
            }
            this.map.setCenter([lng, lat])
            this.addCustomerMarker(lng, lat, result.center.name || 'Customer Center')
            // 加载标记点
            this.getMarkerFn()
          } else {
            // 客户没有定位，显示提示并尝试获取用户当前位置
            this.handleNoCustomerLocation()
          }
        } else {
          // 客户没有定位，显示提示并尝试获取用户当前位置
          this.handleNoCustomerLocation()
        }
      }).catch((err) => {
        console.warn('[middleMap] loadCustomerCenter failed:', err)
        // 请求失败，使用默认位置
        this.applyDefaultLocation()
      })
    },
    // 处理客户无定位的情况
    handleNoCustomerLocation () {
      this.showLocationTip = true
      // 5秒后自动关闭提示并继续定位流程
      setTimeout(() => {
        this.showLocationTip = false
        this.locateBrowserAndLoadMarkers()
      }, 5000)
    },
    // 获取用户浏览器当前位置
    locateBrowserAndLoadMarkers () {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const coords = {
              lng: position.coords.longitude,
              lat: position.coords.latitude,
              name: this.$t('home.dashboard.current_location') || '当前位置',
              id: this.scopeId
            }
            this.customerPosition = coords
            this.map.setCenter([coords.lng, coords.lat])
            this.addCustomerMarker(coords.lng, coords.lat, coords.name)
            // 加载标记点
            this.getMarkerFn()
          },
          error => {
            console.warn('浏览器定位失败，使用默认位置', error)
            this.applyDefaultLocation()
          }
        )
      } else {
        console.warn('浏览器不支持地理定位，使用默认位置')
        this.applyDefaultLocation()
      }
    },
    // 应用默认位置
    applyDefaultLocation () {
      const defaultLng = -123.1192
      const defaultLat = 49.2638
      this.customerPosition = {
        lng: defaultLng,
        lat: defaultLat,
        name: this.$t('home.dashboard.default_location') || '默认位置',
        id: this.scopeId
      }
      this.map.setCenter([defaultLng, defaultLat])
      this.addCustomerMarker(defaultLng, defaultLat, this.customerPosition.name)
      // 加载标记点
      this.getMarkerFn()
    },
    addCustomerMarker (lng, lat, name) {
      if (this.customerMarker) {
        this.customerMarker.remove()
      }
      const el = document.createElement('div')
      el.className = 'customer-map-marker marker-selected'
      el.dataset.markerType = 'customer'
      el.dataset.markerId = this.scopeId
      this.selectedMarkerElement = el
      this.customerMarkerElement = el
      el.innerHTML = `
        <div class="customer-map-marker__dot">
          <i class="iconfont icon-zhuzhaiqu" style="font-size: 40px;"></i>
        </div>
        <div class="customer-map-marker__label">${name}</div>`
      const popupContent = `
        <div class="popup-card">
          <div class="popup-header">
            <div class="popup-icon" style="background:linear-gradient(135deg,#304e72,#ffffff);">
              <i class="iconfont icon-zhuzhaiqu" style="font-size:18px;"></i>
            </div>
            <div class="popup-info">
              <div class="popup-title">${name}</div>
              <div class="popup-subtitle">${this.$t('map.headquarters')}</div>
            </div>
          </div>
          <div class="popup-stats">
            <div class="popup-stat">
              <span class="popup-stat-value">${lat.toFixed(4)}</span>
              <span class="popup-stat-label">${this.$t('map.latitude')}</span>
            </div>
            <div class="popup-stat">
              <span class="popup-stat-value">${lng.toFixed(4)}</span>
              <span class="popup-stat-label">${this.$t('map.longitude')}</span>
            </div>
          </div>
        </div>`
      const popup = new mapboxgl.Popup({ offset: [0, -35], className: 'custom-popup', anchor: 'bottom' })
        .setHTML(popupContent)
      popup.on('close', () => {
        this.onPopupClose('customer', { lng, lat, name })
      })
      popup.on('open', () => {
        bus.$emit('checkScope', { scopeId:this.scopeId })
        this.bindPopupActions(popup, { lng, lat, name })
      })
      this.customerMarker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(this.map)
      el.addEventListener('click', (e) => {
        this.map.setCenter([lng, lat])
        this.handleMapMarkerClick(0, this.customerPosition, el)
        e.stopPropagation()
        if (this.openPopup) {
          this.popupData.remove()
        }
        this.openPopup = true
        this.popupData = popup
        if (popup.isOpen()) {
          popup.remove()
        } else {
          popup.addTo(this.map)
        }
      })
    },
    getMarkerFn () {
      mapTrackPosition({ sceneId: this.sceneId,scopeId:this.scopeId }).then(({ result }) => {
        if (result && result.scene) {
          this.sceneMapList = result.scene
          this.loadSceneMapMarker()
        }
        if (result && result.carrier) {
          this.traceAssetList = result.carrier
          this.loadAssetMarker()
        }
      })
    },
    loadAssetMarker () {
      this.clearMarkers(this.assetMarkers)
      this.assetMarkerElements = []
      if (this.traceAssetList.length > 0) {
        this.traceAssetList.forEach(marker => {
          const itemLng = parseFloat(marker.lng)
          const itemLat = parseFloat(marker.lat)
          if (!isNaN(itemLng) && !isNaN(itemLat) && itemLng !== 0 && itemLat !== 0) {
            const el = document.createElement('div')
            el.className = 'customer-map-marker'
            el.dataset.markerType = 'asset'
            el.dataset.markerId = marker.carrierId || marker.id
            el.innerHTML = `
<div class="customer-map-marker__dot">
  <i class="iconfont icon-a-11-xianghuozhuizong" style="font-size: 40px;"></i>
</div>
<div class="customer-map-marker__label">${marker.keycode}</div>`

            const sampleValueHtml = this.buildSampleValueHtml(marker.sample_value, marker.carrierId || marker.id)

            const popupContent = `
              <div class="popup-card">
                <div class="popup-header">
                  <div class="popup-icon" style="background:linear-gradient(135deg,#1e57e8,#ffffff);">
                    <i class="iconfont icon-a-11-xianghuozhuizong" style="font-size:18px;"></i>
                  </div>
                  <div class="popup-info">
                    <div class="popup-title">${marker.keycode}</div>
                    <div class="popup-transport">${marker.transport === 1 ? this.$t('map.moving') : ''}</div>
                  </div>
                </div>
                <div class="popup-stats">
                  <div class="popup-stat">
                    <span class="popup-stat-value">${marker.lat ? marker.lat.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.latitude')}</span>
                  </div>
                  <div class="popup-stat">
                    <span class="popup-stat-value">${marker.lng ? marker.lng.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.longitude')}</span>
                  </div>
                </div>
                ${sampleValueHtml}
                <div class="popup-actions">
                  ${marker.transport === 1 ? `<button class="popup-btn track-btn" data-action="viewTrack" data-id="${marker.carrierId || marker.id}" data-name="${marker.keycode}">${this.$t('map.view_track')}</button>` : ''}
                  <button class="popup-btn secondary" data-action="assectDetail" data-id="${marker.id}" data-name="${marker.keycode}" data-type="${marker.typeId}">${this.$t('map.details')}</button>
                </div>
              </div>`
              // <button class="popup-btn primary" data-action="viewTrace" data-name="${marker.keycode}" data-transport="${marker.transport}" data-id="${marker.id}">查看轨迹</button>
            const popup = new mapboxgl.Popup({ offset: [0, -35], className: 'custom-popup', anchor: 'bottom' })
              .setHTML(popupContent)
            popup.on('close', () => {
              this.onPopupClose('asset', marker)
            })
            popup.on('open', () => {
              this.bindPopupActions(popup, marker)
            })
            const mapboxMarker = new mapboxgl.Marker(el)
              .setLngLat([itemLng, itemLat])
              .setPopup(popup)
              .addTo(this.map)

            el.addEventListener('click', (e) => {
              this.map.setCenter([marker.lng, marker.lat ])
              this.handleMapMarkerClick(2, marker, el)
              e.stopPropagation()
              if (this.openPopup) {
                this.popupData.remove()
              }
              this.openPopup = true
              this.popupData = popup
              if (popup.isOpen()) {
                popup.remove()
              } else {
                popup.addTo(this.map)
              }
            })

            this.assetMarkers.push({ marker: mapboxMarker, el })
            this.assetMarkerElements.push({ id: marker.carrierId || marker.id, element: el })
          }
        })
      }
    },
    loadSceneMapMarker () {
      this.clearMarkers(this.sceneMarkers)
      this.sceneMarkerElements = []
      if (this.sceneMapList.length > 0) {
        this.sceneMapList.forEach(marker => {
          const itemLng = parseFloat(marker.longitude)
          const itemLat = parseFloat(marker.latitude)
          if (!isNaN(itemLng) && !isNaN(itemLat) && itemLng !== 0 && itemLat !== 0) {
            const el = document.createElement('div')
            el.className = 'customer-map-marker'
            el.dataset.markerType = 'scene'
            el.dataset.markerId = marker.id
            el.innerHTML = `
<div class="customer-map-marker__dot">
  <i class="iconfont icon-wuliuzhongxin" style="font-size: 35px;"></i>
</div>
<div class="customer-map-marker__label">${marker.name}</div>`

            const popupContent = `
              <div class="popup-card">
                <div class="popup-header">
                  <div class="popup-icon" style="background:linear-gradient(135deg,#10b981,#059669);">
                    <i class="iconfont icon-wuliuzhongxin" style="font-size:18px;"></i>
                  </div>
                  <div class="popup-info">
                    <div class="popup-title">${marker.name}</div>
                  </div>
                </div>
                <div class="popup-stats">
                  <div class="popup-stat">
                    <span class="popup-stat-value">${marker.latitude ? marker.latitude.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.latitude')}</span>
                  </div>
                  <div class="popup-stat">
                    <span class="popup-stat-value">${marker.longitude ? marker.longitude.toFixed(4) : '--'}</span>
                    <span class="popup-stat-label">${this.$t('map.longitude')}</span>
                  </div>
                </div>
                <div class="popup-actions">
                  <button class="popup-btn primary" data-action="floorPlan" data-id="${marker.id}">${this.$t('map.viewFloorPlan')}</button>
                </div>
              </div>`
            const popup = new mapboxgl.Popup({ offset: [0, -35], className: 'custom-popup', anchor: 'bottom' })
              .setHTML(popupContent)
            popup.on('close', () => {
              this.onPopupClose('scene', marker)
            })
            popup.on('open', () => {
              this.bindPopupActions(popup, marker)
            })
            const mapboxMarker = new mapboxgl.Marker(el)
              .setLngLat([itemLng, itemLat])
              .setPopup(popup)
              .addTo(this.map)

            el.addEventListener('click', (e) => {
              this.map.setCenter([marker.longitude, marker.latitude ])
              this.handleMapMarkerClick(1, marker, el)
              e.stopPropagation()
              if (this.openPopup) {
                this.popupData.remove()
              }
              this.openPopup = true
              this.popupData = popup
              if (popup.isOpen()) {
                popup.remove()
              } else {
                popup.addTo(this.map)
              }
            })

            this.sceneMarkers.push({ marker: mapboxMarker, el })
            this.sceneMarkerElements.push({ id: marker.id, element: el })
          }
        })
      }
    },

    onPopupClose (type, data) {
      this.openPopup = false
    },

    // 构建取样值HTML - 带轮播功能，参考 temperature-monitor.html 样式
    buildSampleValueHtml (sampleValue, markerId) {
      if (!sampleValue) return ''

      let data = sampleValue
      if (typeof sampleValue === 'string') {
        try {
          data = JSON.parse(sampleValue)
        } catch (e) {
          return ''
        }
      }

      const tempArr = data.temp || []
      const humArr = data.hum || []

      if (tempArr.length === 0 && humArr.length === 0) return ''

      // 合并温度和湿度数据
      const sampleItems = [
        ...humArr.map(item => ({ ...item, code: item.code || 'hum' })),
        ...tempArr.map(item => ({ ...item, code: item.code || 'temp' }))
      ]

      if (sampleItems.length === 0) return ''

      // 为每个取样项生成轮播内容
      let slidesHtml = ''
      sampleItems.forEach((item, index) => {
        const label = item.code === 'temp' ? this.$t('map.temperature') : this.$t('map.humidity')
        const time = item.time ? this.formatSampleTime(item.time) : ''
        const rangeText = item.isRange === 0 && item.min !== undefined && item.max !== undefined
          ? `${item.min}${this.getUnit(item.code)} ~ ${item.max}${this.getUnit(item.code)}`
          : (item.code === 'temp' ? this.$t('integration.unrelated_temperature_range') : this.$t('integration.unrelated_humidity_range'))

        // 获取 itemMap 中的所有值
        let itemMapItems = []
        if (item.itemMap && item.itemMap.length > 0) {
          // 按时间排序
          itemMapItems = [...item.itemMap].sort((a, b) => b.time - a.time)
        }

        // 主值
        let mainValue = '--'
        const unit = this.getUnit(item.code)
        if (itemMapItems.length > 0) {
          mainValue = this.formatTempValue(itemMapItems[0].val, unit)
        }


        // 构建单个滑块
        slidesHtml += `
          <div class="sample-slide" data-index="${index}" style="${index === 0 ? '' : 'display:none;'}">
            <div class="sample-header-bar">
              <i class="iconfont ${item.code === 'temp' ? 'icon-wenduji' : 'icon-wenshiduji'}"></i>
              <span class="sample-header-time">${time}</span>
            </div>
            <div class="sample-range-banner">${rangeText}</div>
            <div class="sample-main-display">
              <span class="sample-main-value">${mainValue}</span>
              <span class="sample-main-unit">${unit}</span>
            </div>
            <div class="sample-records">
              ${this.buildSampleItemListHtml(itemMapItems, item.code)}
            </div>
          </div>`
      })

      // 构建完整的轮播HTML
      const showNav = sampleItems.length > 1
      let html = `
        <div class="popup-sample-carousel">
          ${showNav ? '<button class="sample-nav-btn sample-nav-prev" data-action="prev-sample">‹</button>' : ''}
          <div class="sample-slides-wrapper">
            ${slidesHtml}
          </div>
          ${showNav ? '<button class="sample-nav-btn sample-nav-next" data-action="next-sample">›</button>' : ''}
        </div>`
      return html
    },

    // 构建取样值项目列表HTML
    buildSampleItemListHtml (itemMapItems, code) {
      if (!itemMapItems || itemMapItems.length === 0) {
        return '<div class="sample-empty">---</div>'
      }

      let html = ''
      itemMapItems.forEach(item => {
        const unit = this.getUnit(code)
        const value = this.formatTempValue(item.val, unit)
        const no = item.no || ''
        const type = item.type
        const time = item.time ? this.formatSampleTime(item.time) : '---'

        // 根据 type 获取图标
        const iconClass = type !== undefined ? this.sensorTypeConvert(type).icon : (code === 'temp' ? 'icon-wenduji' : 'icon-wenshiduji')

        html += `
          <div class="sample-record-item">
            <div class="sample-record-left">
              <div class="sample-device-icon">
                <i class="iconfont ${iconClass}"></i>
              </div>
              <div class="sample-device-info">
                <div class="sample-device-id">${no}</div>
                <div class="sample-device-time">${time}</div>
              </div>
            </div>
            <div class="sample-record-right">
              <span class="sample-temp-reading">${value} ${unit}</span>
            </div>
          </div>`
      })
      return html
    },

    // 格式化温度值
    formatTempValue (val, code) {
      if (val === null || val === undefined || val === '') return '--'
      const numVal = Number(val)
      if (isNaN(numVal)) return '--'

      const result = TempUtils.convertToCelsius(numVal, code)
      return `${result}`
    },

    // 格式化取样时间
    formatSampleTime (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 获取单位
    getUnit (code) {
      const { unit } = this.$store.state
      if (unit && unit[code]) {
        return unit[code].name || ''
      }
      return code === 'temp' ? '°C' : '%RH'
    },

    // 动态挂载取样值组件 - 已删除

    bindPopupActions (popup, marker) {
      const popupEl = popup.getElement()
      const buttons = popupEl.querySelectorAll('.popup-btn')
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          const action = btn.dataset.action
          const id = btn.dataset.id
          if (action === 'checkScope') {
            console.log(action);

          } else if (action === 'floorPlan') {
            bus.$emit('scenePlanDetail', { id, marker })
           } else if (action === 'viewTrace') {
            const transport = btn.dataset.transport
            if (transport === '1') {
              bus.$emit('viewRealTimeTrace', { id, marker })
            } else {
              bus.$emit('viewHistoryTrace', { id, marker })
            }
          } else if (action === 'viewTrack') {
            const name = btn.dataset.name
            this.handleViewTrack(id, name)
          }   else if (action === 'assectDetail') {
            const name = btn.dataset.name
            const type = btn.dataset.type
            goNewWindows({
              path: '/newCarrierDetail',
              way: '_blank',
              query: {
                keycode: name,
                type: type,
                id: id
              }
            })
          }
          popup.remove()
        })
      })

      // 绑定轮播按钮事件
      const navBtns = popupEl.querySelectorAll('.sample-nav-btn')
      navBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.handleSampleNavClick(popupEl, btn.dataset.action)
        })
      })
    },

    // 处理取样值轮播导航
    handleSampleNavClick (popupEl, action) {
      const slides = popupEl.querySelectorAll('.sample-slide')
      if (slides.length <= 1) return

      // 找到当前显示的滑块
      let currentIndex = -1
      slides.forEach((slide, index) => {
        if (slide.style.display !== 'none' && currentIndex === -1) {
          currentIndex = index
        }
      })
      if (currentIndex === -1) currentIndex = 0

      // 计算新的索引
      let newIndex = currentIndex
      if (action === 'prev-sample') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1
      } else if (action === 'next-sample') {
        newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0
      }

      // 隐藏所有滑块
      slides.forEach(slide => {
        slide.style.display = 'none'
      })

      // 显示新的滑块
      slides[newIndex].style.display = 'block'

      // 更新滑块内的数据索引和显示
      const currentContainer = slides[currentIndex].querySelector('.sample-values-container')
      const newContainer = slides[newIndex].querySelector('.sample-values-container')

      if (currentContainer && newContainer) {
        // 更新计数
        const header = slides[newIndex].querySelector('.sample-header')
        const itemMapStr = newContainer.dataset.itemMap
        try {
          const itemMap = JSON.parse(itemMapStr)
          const countSpan = header.querySelector('.sample-count')
          if (countSpan && itemMap.length > 0) {
            const currentSlideIndex = parseInt(newContainer.dataset.currentSlideIndex || '0') + 1
            countSpan.textContent = `${currentSlideIndex}/${itemMap.length}`
          }
        } catch (e) {
          // ignore
        }
      }
    },

    clearMarkers (mapMarkersData) {
      mapMarkersData.forEach(({ marker }) => {
        marker.remove()
      })
    },
    // 点击地图标记时，选中对应的树节点
    handleMapMarkerClick (type, markerData, element) {
      // 清除之前选中的标记样式
      if (this.selectedMarkerElement) {
        this.selectedMarkerElement.classList.remove('marker-selected')
      }

      // 设置当前选中的标记样式
      if (element) {
        element.classList.add('marker-selected')
        this.selectedMarkerElement = element
      }

      let nodeId = null;

      // type 0: 客户范围/scope, 1: 场景/scene, 2: 资产/carrier
      if (type === 0) {
        nodeId = this.scopeId;
      } else if (type === 1) {
        // 场景标记
        nodeId = markerData.id;
      } else if (type === 2) {
        // 资产标记
        nodeId = markerData.carrierId || markerData.id;
      }

      // 通过 bus 发送事件，让树组件选中对应节点
      if (nodeId) {
        bus.$emit('selectMapTreeNode', { type, nodeId });
      }
    },
    // 树节点选中时，设置对应标记的选中状态并打开弹框
    handleTreeNodeSelected (row) {
      if (!row) return

      const setSelectedMarker = (element) => {
        if (this.selectedMarkerElement) {
          this.selectedMarkerElement.classList.remove('marker-selected')
        }
        if (element) {
          element.classList.add('marker-selected')
          this.selectedMarkerElement = element
        }
      }

      // 设置地图中心的通用方法
      const setMapCenter = (lng, lat) => {
        if (this.map && !isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
          this.map.flyTo({
            center: [lng, lat],
            zoom: 14,
            duration: 1000
          })
        }
      }

      // 打开弹框的通用方法
      const openPopup = (markerObj) => {
        if (!markerObj || !markerObj.marker) return
        const marker = markerObj.marker
        const popup = marker.getPopup()
        if (popup) {
          // 先关闭当前打开的弹框
          if (this.openPopup && this.popupData) {
            this.popupData.remove()
          }
          // 打开新的弹框
          this.openPopup = true
          this.popupData = popup
          popup.addTo(this.map)
        }
      }

      if (row.nodeType !== 0 && row.nodeType !== 1) {
        // 客户节点或场景节点
        const sceneId = row.level === 0 ? row.scopeId : row.sceneId
        if (row.funcType === 0) {
          // 客户节点 - 选中客户标记
          setSelectedMarker(this.customerMarkerElement)
          // 设置地图中心到客户位置
          if (this.customerPosition) {
            setMapCenter(this.customerPosition.lng, this.customerPosition.lat)
          }
          // 打开客户弹框
          if (this.customerMarker) {
            openPopup({ marker: this.customerMarker })
          }
          // 触发 checkScope 事件，让右侧面板显示监控概览
          bus.$emit('checkScope', { scopeId: row.scopeId })
        } else {
          // 场景节点 - 查找对应的场景标记
          const markerInfo = this.sceneMarkerElements.find(m => m.id === sceneId)
          if (markerInfo) {
            setSelectedMarker(markerInfo.element)
            // 查找场景数据获取位置
            const sceneData = this.sceneMapList.find(s => s.id === sceneId)
            if (sceneData) {
              setMapCenter(parseFloat(sceneData.longitude), parseFloat(sceneData.latitude))
            }
            // 打开场景弹框
            const sceneMarkerObj = this.sceneMarkers.find(m => m.el === markerInfo.element)
            if (sceneMarkerObj) {
              openPopup(sceneMarkerObj)
            }
          }
        }
      } else if (row.nodeType === 1) {
        // 资产节点 - 查找对应的资产标记
        const markerInfo = this.assetMarkerElements.find(m => m.id === row.carrierId)
        if (markerInfo) {
          setSelectedMarker(markerInfo.element)
          // 查找资产数据获取位置
          const assetData = this.traceAssetList.find(a => a.carrierId === row.carrierId || a.id === row.carrierId)
          if (assetData) {
            setMapCenter(parseFloat(assetData.lng), parseFloat(assetData.lat))
          }
          // 打开资产弹框
          const assetMarkerObj = this.assetMarkers.find(m => m.el === markerInfo.element)
          if (assetMarkerObj) {
            openPopup(assetMarkerObj)
          }
        }
      }
    },
    // 查看轨迹
    handleViewTrack (carrierId, carrierName) {
      this.trackCarrierId = carrierId
      this.trackCarrierName = carrierName
      this.trackDialogVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.middle-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  margin-right: 5px;

  #newContainer {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

  }

  ::v-deep .info-abnormal {
    font-weight: bolder;
    color: #f87171;
  }

  ::v-deep .info-normal {
    font-weight: bolder;
    color: #e9eef3;
  }

  ::v-deep .el-tabs__header {
    height: 35px;
    line-height: 35px;
    margin-bottom: 10px;
  }

  ::v-deep .el-tabs__item {
    height: 35px;
    line-height: 35px;
  }

  .bread {
    position: absolute;
    top: 480px;
    right: 0px;
    z-index: 99999;
  }

  ::v-deep .el-breadcrumb {
    margin: 0;
    padding: 0;
    height: auto;
    background: transparent;
  }

  ::v-deep .mapboxgl-popup-content {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.1);
    border-radius: 8px;
    padding: 10px 10px;
  }

  ::v-deep .mapboxgl-popup-tip {
    display: none;
  }

  ::v-deep .mapboxgl-ctrl {
    border-radius: 8px;
    overflow: hidden;
  }

  ::v-deep .mapboxgl-ctrl-group {

    border: 1px solid rgba(255,255,255,0.1);
  }

  ::v-deep .mapboxgl-ctrl-group button {
    background: transparent;
    border-color: rgba(255,255,255,0.1);
    color: #e9eef3;

    &:hover {
      background: rgba(255,255,255,0.1);
      color: #fff;
    }
  }

  ::v-deep .mapboxgl-ctrl-group button:not(:last-child) {
    border-bottom-color: rgba(255,255,255,0.1);
  }
}
</style>

<style>
.custom-popup .mapboxgl-popup-content-wrapper {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}
.custom-popup .mapboxgl-popup-content {
  margin: 0;
  width: auto !important;
  padding: 0;
}
.custom-popup .mapboxgl-popup-tip {
  display: none;
}
.popup-card {
  min-width: 220px;
}
.popup-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.popup-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
}
.popup-info { flex: 1; }
.popup-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}
.popup-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}
.popup-transport {
  font-size: 13px;
  color: #34d399;
  margin-top: 2px;
}
.popup-stats {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid rgba(0,0,0,0.06);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  margin-bottom: 10px;
}
.popup-stat {
  flex: 1;
  text-align: center;
}
.popup-stat-value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  font-family: 'JetBrains Mono', monospace;
}
.popup-stat-label {
  display: block;
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
}

.popup-actions {
  display: flex;
  gap: 8px;
}

.popup-btn {
  flex: 1;
  padding: 7px 12px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.popup-btn.primary {
  background: linear-gradient(135deg, #38bdf8, #0284c7);
  color: #fff;
}
.popup-btn.primary:hover {
  box-shadow: 0 0 12px rgba(56,189,248,0.4);
}
.popup-btn.secondary {
  background: rgb(198 198 190 / 0.27);
  color: #0e1012;
}
.popup-btn.secondary:hover {
  background: rgba(255,255,255,0.1);
}
/* 查看轨迹按钮样式 */
.popup-btn.track-btn {
  background: linear-gradient(135deg, #4264fb, #1e57e8);
  color: #fff;
}
.popup-btn.track-btn:hover {
  background: linear-gradient(135deg, #5a7cfc, #3b6fe9);
}
.mapboxgl-marker,
.mapboxgl-marker * {
  transition: none !important;
}
.custom-popup .mapboxgl-popup-close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;

  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
}
.custom-popup .mapboxgl-popup-close-button:hover {

  color: #000;
}

/* ==================== Marker Selected State ==================== */
/* 这些样式需要非 scoped 才能作用于动态创建的 DOM 元素 */

/* 取样值轮播样式 - 参考 temperature-monitor.html */
.popup-sample-carousel {
  position: relative;
  padding: 10px 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
}

.sample-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(30, 75, 168, 0.8);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  z-index: 10;
  opacity: 0.7;
}

.sample-nav-btn:hover {
  background: #1e4ba8;
  opacity: 1;
}

.sample-nav-prev {
  left: 0;
}

.sample-nav-next {
  right: 0;
}

.sample-slides-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.sample-slide {
  width: 100%;
  box-sizing: border-box;
}

/* 顶部状态栏 */
.sample-header-bar {
  display: flex;
  align-items: center;
  padding: 10px 12px 8px;
  background: #fff;
  position: relative;
}

.sample-header-bar .iconfont {
  color: #ff8a3d;
  font-size: 18px;
  flex-shrink: 0;
}

.sample-header-time {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

/* 蓝色横条 - 区间显示 */
.sample-range-banner {
  background: #1e4ba8;
  color: #fff;
  text-align: center;
  padding: 8px 5px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 主值显示区 */
.sample-main-display {
  text-align: center;
  padding: 20px 12px 18px;
  background: #fff;
}

.sample-main-value {
  font-size: 20px;
  font-weight: 300;
  color: #1a1a1a;
  line-height: 1;
  letter-spacing: -1px;
}

.sample-main-unit {
  font-size: 20px;
  font-weight: 300;
  color: #1a1a1a;
  vertical-align: top;
  margin-left: 2px;
}

/* 记录列表 */
.sample-records {
  border-top: 1px solid #f0f0f0;
  background: #fff;
  max-height: 120px;
  overflow-y: auto;
}

.sample-record-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 5px;
  border-bottom: 1px solid #f0f0f0;
}

.sample-record-item:last-child {
  border-bottom: none;
}

.sample-record-left {
  display: flex;
  align-items: center;
  gap: 5px;
}

.sample-device-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7785;
  font-size: 14px;
}

.sample-device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sample-device-id {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 500;
}

.sample-device-time {
  font-size: 11px;
  color: #9aa3ad;
}

.sample-record-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sample-temp-reading {
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
}

.sample-empty {
  text-align: center;
  color: #9ca3af;
  padding: 16px;
  font-size: 13px;
}

/* 客户无定位提示样式 */
.map-location-tip {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%);
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 150;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
  max-width: 90%;
}

.map-location-tip i {
  font-size: 20px;
  color: #f59e0b;
}

.map-location-tip span {
  font-size: 14px;
  color: #92400e;
  font-weight: 500;
}

/* 提示淡入淡出动画 */
.location-tip-fade-enter-active {
  animation: tipFadeIn 0.3s ease-out;
}

.location-tip-fade-leave-active {
  animation: tipFadeOut 0.3s ease-in;
}

@keyframes tipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes tipFadeOut {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
}

.marker-selected .customer-map-marker__dot i {
  color: #f97316 !important;
  animation: marker-pulse 1.5s ease-in-out infinite;
}

.customer-map-marker {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.customer-map-marker__dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: var(--primary-color, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, background 0.2s ease;
}

.customer-map-marker__dot:hover {
  transform: scale(1.15);
}



.customer-map-marker__label {
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--primary-color, #2563eb);
}

@keyframes marker-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
</style>
